import React, { createContext, useCallback, useContext, useReducer } from 'react';

import { removeNull } from 'constants/helper';

const defaultCohortCreationState = {
  name: '',
  selectedOptions: {},
  baseCohort: {},
  visibilityType: 'PRIVATE',
};

const CohortCreationContext = createContext(defaultCohortCreationState);

const actions = {
  // this action will be used when we allow editing of existing cohorts.
  INITIALIZE_STATE: 'INITIALIZE_STATE',
  UPDATE_KEYS_VALUE: "UPDATE_KEY'S_VALUE",
  UPDATE_KEYS_DATA: 'UPDATE_KEYS_DATA',
  APPEND_KEYS_VALUE: "APPEND_KEY'S_VALUE",
  DELETE_KEYS_VALUE: "DELETE_KEY'S_VALUE",
  DELETE_COMPLETE_KEY_VALUE: 'DELETE_COMPLETE_KEY_VALUE',
  RESET_STATE: 'RESET_STATE',
  APPEND_NESTED_KEY_VALUE: 'APPEND_NESTED_KEY_VALUE',
  DELETE_NESTED_KEY_VALUE: 'DELETE_NESTED_KEY_VALUE',

  // used for setting Cohort Name.
  SET_COHORT_NAME: 'SET_COHORT_NAME',

  // base Cohort actions.
  UPDATE_BASE_COHORT: 'UPDATE_BASE_COHORT',

  // update visibilityType
  UPDATE_VISIBILITY: 'UPDATE_VISIBILITY',
};

const actionsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case actions.INITIALIZE_STATE:
      return {
        ...state,
      };
    case actions.UPDATE_KEYS_VALUE:
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          [payload.key]: {
            [payload.valueObj.id]: payload.valueObj,
          },
        },
      };
    case actions.UPDATE_KEYS_DATA:
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          [payload.key]: {
            ...state.selectedOptions[payload.key],
            [payload.type]: payload.valueObj,
          },
        },
      };
    case actions.APPEND_KEYS_VALUE: {
      const { key, valueObj } = payload;
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          [key]: state.selectedOptions[key]
            ? {
                ...state.selectedOptions[key],
                [valueObj.id]: valueObj,
              }
            : { [valueObj.id]: valueObj },
        },
      };
    }
    case actions.APPEND_NESTED_KEY_VALUE: {
      const { parentKey, childKey, valueObj } = payload;
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          [parentKey]: {
            ...state.selectedOptions[parentKey],
            [childKey]:
              state.selectedOptions[parentKey] && state.selectedOptions[parentKey][childKey]
                ? {
                    ...state.selectedOptions[parentKey][childKey],
                    [valueObj.id]: valueObj,
                  }
                : { [valueObj.id]: valueObj },
          },
        },
      };
    }

    case actions.DELETE_KEYS_VALUE: {
      const { key, valueObj } = payload;

      const nonNullObject = removeNull({
        ...state.selectedOptions[key],
        [valueObj.id]: null,
      });

      return {
        ...state,
        selectedOptions: removeNull({
          ...state.selectedOptions,
          [key]: Object.keys(nonNullObject).length > 0 ? nonNullObject : null,
        }),
      };
    }

    case actions.DELETE_COMPLETE_KEY_VALUE: {
      const { key } = payload;

      return {
        ...state,
        selectedOptions: removeNull({
          ...state.selectedOptions,
          [key]: null,
        }),
      };
    }

    case actions.DELETE_NESTED_KEY_VALUE: {
      const { parentKey, childKey, valueObj } = payload;

      const nonNullObject = removeNull({
        ...state.selectedOptions[parentKey][childKey],
        [valueObj.id]: null,
      });

      return {
        ...state,
        selectedOptions: {
          [parentKey]: removeNull({
            ...state.selectedOptions[parentKey],
            [childKey]: Object.keys(nonNullObject).length > 0 ? nonNullObject : null,
          }),
        },
      };
    }

    case actions.SET_COHORT_NAME:
      return {
        ...state,
        name: payload.name,
      };
    case actions.RESET_STATE:
      return { ...defaultCohortCreationState, baseCohort: { uuid: payload.baseCohortId } };

    case actions.UPDATE_BASE_COHORT: {
      return {
        ...state,
        baseCohort: {
          uuid: payload.uuid,
        },
      };
    }

    case actions.UPDATE_VISIBILITY: {
      return {
        ...state,
        visibilityType: payload.visibilityType,
      };
    }
    default:
      return state;
  }
};

export const CohortCreationContextProvider = ({ children }) => {
  const [state, cohortCreationDispatch] = useReducer(actionsReducer, defaultCohortCreationState);

  const initializeState = useCallback((initialValues) => {
    cohortCreationDispatch({
      type: actions.INITIALIZE_STATE,
      payload: initialValues,
    });
  }, []);

  const updateSelectedValue = useCallback((key, valueObj) => {
    cohortCreationDispatch({
      type: actions.UPDATE_KEYS_VALUE,
      payload: {
        key,
        valueObj,
      },
    });
  }, []);

  const updateSelectedData = useCallback((key, type, valueObj) => {
    cohortCreationDispatch({
      type: actions.UPDATE_KEYS_DATA,
      payload: {
        key,
        type,
        valueObj,
      },
    });
  }, []);

  const appendSelectedValue = useCallback((key, valueObj) => {
    cohortCreationDispatch({
      type: actions.APPEND_KEYS_VALUE,
      payload: {
        key,
        valueObj,
      },
    });
  }, []);

  const appendNestedSelectedValue = useCallback((parentKey, childKey, valueObj) => {
    cohortCreationDispatch({
      type: actions.APPEND_NESTED_KEY_VALUE,
      payload: {
        parentKey,
        childKey,
        valueObj,
      },
    });
  }, []);

  const deleteSelectedValue = useCallback((key, valueObj) => {
    cohortCreationDispatch({
      type: actions.DELETE_KEYS_VALUE,
      payload: {
        key,
        valueObj,
      },
    });
  }, []);

  const deleteSelectedKeyValue = useCallback((key) => {
    cohortCreationDispatch({
      type: actions.DELETE_COMPLETE_KEY_VALUE,
      payload: {
        key,
      },
    });
  }, []);

  const deleteNestedSelectedValue = useCallback((parentKey, childKey, valueObj) => {
    cohortCreationDispatch({
      type: actions.DELETE_NESTED_KEY_VALUE,
      payload: {
        parentKey,
        childKey,
        valueObj,
      },
    });
  }, []);

  const resetState = useCallback((baseCohortId) => {
    cohortCreationDispatch({
      type: actions.RESET_STATE,
      payload: { baseCohortId },
    });
  }, []);

  const setCohortName = useCallback((name) => {
    cohortCreationDispatch({
      type: actions.SET_COHORT_NAME,
      payload: { name },
    });
  }, []);

  const updateBaseCohort = useCallback((baseCohortUuid) => {
    cohortCreationDispatch({
      type: actions.UPDATE_BASE_COHORT,
      payload: { uuid: baseCohortUuid },
    });
  }, []);

  const updateCohortVisibility = useCallback((visibilityType) => {
    cohortCreationDispatch({
      type: actions.UPDATE_VISIBILITY,
      payload: { visibilityType },
    });
  }, []);

  return (
    <CohortCreationContext.Provider
      value={{
        state,
        initializeState,
        updateSelectedValue,
        appendSelectedValue,
        deleteSelectedValue,
        updateSelectedData,
        resetState,
        setCohortName,
        updateBaseCohort,
        updateCohortVisibility,
        appendNestedSelectedValue,
        deleteNestedSelectedValue,
        deleteSelectedKeyValue,
      }}
    >
      {children}
    </CohortCreationContext.Provider>
  );
};

export const useCohortCreationContext = () => useContext(CohortCreationContext);
